(() => {
  if (customElements.get('horizontal-scroll-progress')) return;

  // One frame for every progress line on the page: all of them measure first, then all of them
  // write. A write between two measurements would force a layout per line.
  const pending = new Set();
  let frame = null;

  function flush() {
    frame = null;
    const items = Array.from(pending);
    pending.clear();
    const states = items.map((item) => item.measure());
    items.forEach((item, index) => item.apply(states[index]));
  }

  function schedule(item) {
    pending.add(item);
    if (!frame) frame = requestAnimationFrame(flush);
  }

  class HorizontalScrollProgress extends HTMLElement {
    connectedCallback() {
      this.targetId = this.dataset.target;
      this.itemSelector = this.dataset.itemSelector || ':scope > *';
      this.rail = this.targetId ? document.getElementById(this.targetId) : null;
      if (!this.rail) return;

      this.value = this.querySelector('.horizontal-scroll-progress__value');
      if (!this.value) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.rail.addEventListener('scroll', () => this.requestUpdate(), { passive: true, signal });
      window.addEventListener('resize', () => this.requestUpdate(), { passive: true, signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.requestUpdate());
        this.resizeObserver.observe(this.rail);
      }

      this.mutationObserver = new MutationObserver(() => this.requestUpdate());
      this.mutationObserver.observe(this.rail, { childList: true, subtree: true });
      this.requestUpdate();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      pending.delete(this);
    }

    requestUpdate() {
      schedule(this);
    }

    // Reads only. Returns null when the line is hidden (desktop, or nothing to scroll).
    measure() {
      if (!this.rail || !this.value || !this.rail.isConnected) return null;
      if (!window.matchMedia('(max-width: 767px)').matches) return null;

      const scrollableWidth = Math.max(this.rail.scrollWidth - this.rail.clientWidth, 0);
      if (scrollableWidth <= 1) return null;

      const firstItem = this.rail.querySelector(this.itemSelector);
      const railRect = this.rail.getBoundingClientRect();
      const firstRect = firstItem?.getBoundingClientRect();
      const isRtl = getComputedStyle(this.rail).direction === 'rtl';
      const scrollOffset = firstRect
        ? isRtl
          ? Math.max(firstRect.right - railRect.right, 0)
          : Math.max(railRect.left - firstRect.left, 0)
        : Math.abs(this.rail.scrollLeft);
      const visibleProgress = this.rail.scrollWidth > 0 ? this.rail.clientWidth / this.rail.scrollWidth : 1;
      const scrollProgress = Math.min(scrollOffset / scrollableWidth, 1);
      return Math.min(visibleProgress + scrollProgress * (1 - visibleProgress), 1).toFixed(4);
    }

    // Writes only, and only what changed.
    apply(progress) {
      const hidden = progress === null;
      if (this.hidden !== hidden) this.hidden = hidden;
      if (hidden || !this.value) return;
      if (this.value.style.getPropertyValue('--horizontal-scroll-progress') !== progress) {
        this.value.style.setProperty('--horizontal-scroll-progress', progress);
      }
    }
  }

  customElements.define('horizontal-scroll-progress', HorizontalScrollProgress);
})();
